import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import type { Socket } from 'socket.io-client'

interface Notification {
  id: string
  type: 'document_processed' | 'risk_detected' | 'audit_complete' | 'info' | 'warning' | 'error' | 'success'
  title: string
  message: string
  severity: 'info' | 'warning' | 'error' | 'success'
  namespace?: string
  timestamp: Date
  read: boolean
}

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  isConnected: boolean
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  clearNotifications: () => void
  removeNotification: (id: string) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || ''
const SOCKET_ENABLED = import.meta.env.VITE_SOCKET_ENABLED === 'true'

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      read: false
    }
    setNotifications(prev => [newNotification, ...prev].slice(0, 50))
  }, [])

  useEffect(() => {
    // Only connect if socket is explicitly enabled and URL is configured.
    // socket.io-client is dynamically imported so the ~40 KiB realtime chunk
    // stays out of the landing-page bundle.
    if (!SOCKET_ENABLED || !SOCKET_URL) {
      return
    }

    let activeSocket: Socket | null = null
    let cancelled = false

    import('socket.io-client').then(({ io }) => {
      if (cancelled) return

      const newSocket = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        reconnectionAttempts: 3,
        reconnectionDelay: 2000,
        timeout: 5000
      })

      newSocket.on('connect', () => {
        setIsConnected(true)
      })

      newSocket.on('disconnect', () => {
        setIsConnected(false)
      })

      newSocket.on('connected', (data) => {
        addNotification({
          type: 'info',
          title: 'Connected',
          message: data.message || 'WebSocket connection established',
          severity: 'success'
        })
      })

      newSocket.on('notification', (data) => {
        const severityMap: Record<string, Notification['severity']> = {
          success: 'success',
          warning: 'warning',
          error: 'error',
          info: 'info'
        }

        addNotification({
          type: data.type || 'info',
          title: data.title || 'Notification',
          message: data.message || '',
          severity: severityMap[data.severity] || 'info',
          namespace: data.namespace
        })
      })

      newSocket.on('connect_error', () => {
        setIsConnected(false)
      })

      activeSocket = newSocket
      setSocket(newSocket)
    })

    return () => {
      cancelled = true
      if (activeSocket) activeSocket.close()
    }
  }, [addNotification])

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    )
  }, [])

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }, [])

  const clearNotifications = useCallback(() => {
    setNotifications([])
  }, [])

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  const unreadCount = notifications.filter(n => !n.read).length

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      isConnected,
      addNotification,
      markAsRead,
      markAllAsRead,
      clearNotifications,
      removeNotification
    }}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
